import { useSyncExternalStore } from 'react';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';
import { ADMIN_LOCKED, CATALOG_VERSION, allowedBeforeUpgrade } from '../constants/permissionCatalog';
import { viewAsRole } from '../utils/viewAsRole';

/**
 * The user the app's checks see: the signed-in user, or - while an admin uses "Show the app as this role" - that role
 * (its permissions, not the admin's; nothing is saved).
 */
export const effectiveUserOf = (currentUser, viewing) => {
  if (!currentUser || !viewing || currentUser.role !== 'admin') return currentUser;
  return { ...currentUser, role: viewing.roleId, permissions: viewing.permissions, permissionsVersion: CATALOG_VERSION };
};

/**
 * Does `user` hold `permission`? The admin holds every one. A permission the catalogue added (permissionCatalog.js) is
 * also held, until the user's profile is upgraded (permissionsVersion < CATALOG_VERSION), by whoever meets the rule
 * that decided that button the day before - no one is locked out while the roles are being upgraded.
 */
export const holds = (user, permission) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (ADMIN_LOCKED.has(permission)) return false;          // the Admin role's own powers: no list gives them
  const list = Array.isArray(user.permissions) ? user.permissions : null;
  if (!list) return false;
  if (list.includes(permission)) return true;
  return (Number(user.permissionsVersion) || 1) < CATALOG_VERSION && allowedBeforeUpgrade(permission, list);
};

export const usePermissions = () => {
  const { currentUser } = useAuth();
  const viewing = useSyncExternalStore(viewAsRole.subscribe, viewAsRole.get, () => null);
  const user = effectiveUserOf(currentUser, viewing);

  const hasPermission = (permission) => {
    if (user && user.role !== 'admin' && !Array.isArray(user.permissions)) {
      logger.warn(`⚠️ User ${user.email} has NO permissions array!`);
    }
    return holds(user, permission);
  };

  const hasAnyPermission = (permissions) => (permissions || []).some((p) => holds(user, p));

  const hasAllPermissions = (permissions) => (permissions || []).every((p) => holds(user, p));

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    permissions: user?.permissions || [],
    // the Admin role as the checks see it: false while an admin looks at the app as another role
    isAdmin: user?.role === 'admin',
    // the role the admin is looking through ("Show the app as this role"), or null
    viewingAs: currentUser?.role === 'admin' && viewing ? viewing : null,
  };
};
